import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BetaBadge } from "@/components/BetaBadge";
import { KalibLogo } from "@/components/KalibLogo";
import { Settings, LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin · Account requests.
 *
 * Two views in one page:
 *   1. Demand summary — one row per normalised account, count of
 *      requests, latest status badge.
 *   2. All requests — flat table, newest first, with admin controls
 *      to set status, link a canonical_account_id, and activate +
 *      notify the requester.
 *
 * The canonical_account_id field expects a slug from the live accounts
 * catalog (e.g. "deutsche_bank"). Backend validates it exists before
 * persisting; activation requires it to be set.
 */
const STATUS_LABELS = {
  submitted: "Submitted",
  duplicate: "Duplicate",
  under_review: "Under review",
  planned: "Planned",
  active: "Active",
  unsupported: "Unsupported",
  rejected: "Rejected",
};

const STATUS_BADGE_STYLE = {
  submitted:    "bg-zinc-100 text-zinc-700 border-zinc-300",
  duplicate:    "bg-amber-50 text-amber-800 border-amber-300",
  under_review: "bg-blue-50 text-blue-800 border-blue-300",
  planned:      "bg-violet-50 text-violet-800 border-violet-300",
  active:       "bg-emerald-50 text-emerald-800 border-emerald-300",
  unsupported:  "bg-zinc-100 text-zinc-500 border-zinc-300",
  rejected:     "bg-rose-50 text-rose-800 border-rose-300",
};

const MOTION_LABELS = {
  acquisition: "Win new",
  retention:   "Grow existing",
  solutioning: "Build solutioning",
  delivery:    "Deliver",
};

function formatTimestamp(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function AdminAccountRequestsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [requests, setRequests] = useState([]);
  const [grouped, setGrouped] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: reqRes }, { data: accRes }] = await Promise.all([
        API.get("/admin/account-requests"),
        API.get("/accounts"),
      ]);
      setRequests(reqRes.requests || []);
      setGrouped(reqRes.grouped || []);
      setAccounts(accRes.accounts || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't load requests.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchData();
  }, [user, fetchData]);

  if (user === null) {
    navigate("/signin");
    return null;
  }
  if (!user) return null;
  if (!user.is_admin) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <p className="text-sm text-zinc-700">Admin only.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="admin-account-requests-page">
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="block" data-testid="admin-account-requests-logo-link" aria-label="Kalib AI home">
              <KalibLogo variant="light" height={28} testId="admin-account-requests-header-logo" />
            </Link>
            <BetaBadge />
            <span className="text-[11px] font-medium uppercase tracking-widest text-amber-700 border border-amber-700/30 px-1.5 py-0.5 leading-none">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/admin/accounts" className="text-[13px] text-zinc-900 hover:text-zinc-950">Accounts</Link>
            <Link to="/admin/preview" className="text-[13px] text-zinc-900 hover:text-zinc-950">Preview</Link>
            <Link to="/intelligence" className="text-[13px] text-zinc-900 hover:text-zinc-950">Intelligence</Link>
            <Link to="/settings/profile" className="text-zinc-900 hover:text-zinc-950">
              <Settings className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <button onClick={() => { logout(); navigate("/"); }} className="text-zinc-900 hover:text-zinc-950">
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-medium tracking-tight text-zinc-950 font-heading">Requested accounts</h1>
            <p className="text-sm text-zinc-900 mt-1">Demand from users for accounts not yet on the catalog. Activate when coverage is ready.</p>
          </div>
          <Button
            variant="outline"
            onClick={fetchData}
            disabled={loading}
            className="rounded-none border-zinc-200 hover:bg-zinc-50 text-sm"
            data-testid="admin-account-requests-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} />
            Refresh
          </Button>
        </div>

        {/* ── Demand summary ── */}
        <section className="mb-10" data-testid="account-requests-grouped-section">
          <h2 className="text-[14px] font-semibold uppercase tracking-widest text-zinc-700 mb-3">Demand summary</h2>
          {grouped.length === 0 ? (
            <p className="text-sm text-zinc-500">No requests yet.</p>
          ) : (
            <div className="bg-white border border-zinc-200">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-widest text-zinc-700">
                    <th className="px-4 py-2.5">Account (normalised)</th>
                    <th className="px-4 py-2.5">Requests</th>
                    <th className="px-4 py-2.5">First requested</th>
                    <th className="px-4 py-2.5">Latest status</th>
                    <th className="px-4 py-2.5">Linked to</th>
                  </tr>
                </thead>
                <tbody>
                  {grouped.map((g) => (
                    <tr key={g.account_name_normalized} className="border-b border-zinc-100 last:border-b-0">
                      <td className="px-4 py-2.5 font-medium text-zinc-950">{g.display_name || g.account_name_normalized}</td>
                      <td className="px-4 py-2.5 font-mono text-zinc-900">{g.count}</td>
                      <td className="px-4 py-2.5 text-zinc-600 text-[12px]">{formatTimestamp(g.first_requested_at)}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-block px-1.5 py-0.5 text-[11px] uppercase tracking-wider border ${STATUS_BADGE_STYLE[g.latest_status] || STATUS_BADGE_STYLE.submitted}`}>
                          {STATUS_LABELS[g.latest_status] || g.latest_status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-[12px] text-zinc-600">{g.canonical_account_id || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* ── All requests ── */}
        <section data-testid="account-requests-flat-section">
          <h2 className="text-[14px] font-semibold uppercase tracking-widest text-zinc-700 mb-3">All requests ({requests.length})</h2>
          {requests.length === 0 ? (
            <p className="text-sm text-zinc-500">No requests yet.</p>
          ) : (
            <div className="space-y-3">
              {requests.map((r) => (
                <RequestRow
                  key={r.id}
                  row={r}
                  accounts={accounts}
                  onUpdated={fetchData}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function RequestRow({ row, accounts, onUpdated }) {
  const [status, setStatus] = useState(row.status);
  const [canonicalId, setCanonicalId] = useState(row.canonical_account_id || "");
  const [adminNote, setAdminNote] = useState(row.admin_note || "");
  const [saving, setSaving] = useState(false);

  const dirty =
    status !== row.status ||
    (canonicalId || "") !== (row.canonical_account_id || "") ||
    (adminNote || "") !== (row.admin_note || "");

  const save = async ({ notify = false } = {}) => {
    setSaving(true);
    try {
      const payload = {};
      if (status !== row.status) payload.status = status;
      if ((canonicalId || "") !== (row.canonical_account_id || "")) payload.canonical_account_id = canonicalId || "";
      if ((adminNote || "") !== (row.admin_note || "")) payload.admin_note = adminNote;
      if (notify) {
        payload.status = "active";
        payload.notify = true;
      }
      await API.patch(`/admin/account-requests/${row.id}`, payload);
      toast.success(notify ? "Activated and email sent." : "Saved.");
      onUpdated?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const canonicalIsValid = !canonicalId || accounts.some((a) => a.id === canonicalId);

  return (
    <div className="bg-white border border-zinc-200 p-4" data-testid={`account-request-row-${row.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[15px] font-semibold text-zinc-950">{row.account_name_raw}</p>
            <span className="text-[11px] text-zinc-500">→ {row.account_name_normalized}</span>
            <span className={`inline-block px-1.5 py-0.5 text-[11px] uppercase tracking-wider border ${STATUS_BADGE_STYLE[row.status] || STATUS_BADGE_STYLE.submitted}`}>
              {STATUS_LABELS[row.status] || row.status}
            </span>
          </div>
          <div className="mt-1.5 text-[12px] text-zinc-600 space-x-3">
            <span><strong>{row.requester_first_name || "—"}</strong> · {row.requester_email}</span>
            <span>· {MOTION_LABELS[row.role_family_requested] || row.role_family_requested}</span>
            {row.company_type_snapshot && <span>· {row.company_type_snapshot}</span>}
            {row.seniority_tier_snapshot && <span>· {row.seniority_tier_snapshot}</span>}
            {row.requester_country && (
              <span className="inline-block px-1 py-0 text-[11px] font-mono bg-zinc-100 border border-zinc-200 text-zinc-700 leading-tight" title="Country from request headers">
                {row.requester_country}
              </span>
            )}
            <span>· {formatTimestamp(row.created_at)}</span>
          </div>
          {row.request_note && (
            <p className="mt-2 text-[13px] text-zinc-700 italic border-l-2 border-zinc-200 pl-2">"{row.request_note}"</p>
          )}
          {row.notification_status && (
            <p className="mt-1.5 text-[11px] text-zinc-500">
              Email: <span className="font-mono">{row.notification_status}</span>
              {row.notified_at ? ` · ${formatTimestamp(row.notified_at)}` : ""}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="block">
          <span className="block text-[10px] font-semibold uppercase tracking-widest text-zinc-700 mb-1">Status</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="block w-full px-2 py-1.5 border border-zinc-200 bg-white text-[13px] focus:outline-none focus:border-amber-700"
            data-testid={`account-request-row-${row.id}-status`}
          >
            {Object.entries(STATUS_LABELS).map(([v, label]) => (
              <option key={v} value={v}>{label}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-[10px] font-semibold uppercase tracking-widest text-zinc-700 mb-1">Canonical account id</span>
          <input
            type="text"
            list={`accounts-${row.id}`}
            value={canonicalId}
            onChange={(e) => setCanonicalId(e.target.value.trim())}
            placeholder="e.g. deutsche_bank"
            className={`block w-full px-2 py-1.5 border bg-white text-[13px] font-mono focus:outline-none ${
              canonicalIsValid ? "border-zinc-200 focus:border-amber-700" : "border-rose-400 focus:border-rose-500"
            }`}
            data-testid={`account-request-row-${row.id}-canonical`}
          />
          <datalist id={`accounts-${row.id}`}>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.display_name || a.name} ({a.status})</option>
            ))}
          </datalist>
          {!canonicalIsValid && (
            <span className="block mt-1 text-[10px] text-rose-600">Not in catalog</span>
          )}
        </label>
        <label className="block">
          <span className="block text-[10px] font-semibold uppercase tracking-widest text-zinc-700 mb-1">Admin note</span>
          <input
            type="text"
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            placeholder="Internal — never sent"
            className="block w-full px-2 py-1.5 border border-zinc-200 bg-white text-[13px] focus:outline-none focus:border-amber-700"
            data-testid={`account-request-row-${row.id}-note`}
          />
        </label>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2 flex-wrap">
        <Button
          variant="ghost"
          onClick={() => save()}
          disabled={!dirty || saving || !canonicalIsValid}
          className="rounded-none text-[13px]"
          data-testid={`account-request-row-${row.id}-save`}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button
          onClick={() => save({ notify: true })}
          disabled={saving || !canonicalId || !canonicalIsValid}
          className="bg-amber-700 text-white hover:bg-amber-800 rounded-none text-[13px]"
          data-testid={`account-request-row-${row.id}-activate`}
          title="Sets status to Active, adds the canonical account to the user's followed list (if they have headroom), and emails them."
        >
          {row.notification_status === "sent" ? "Resend email" : "Activate + email"}
        </Button>
      </div>
    </div>
  );
}
